import React from "react";
import { Refine, AuthProvider } from "@pankod/refine-core";
import {
  notificationProvider,
  Layout,
  ReadyPage,
  ErrorComponent,
} from "@pankod/refine-antd";

import "@pankod/refine-antd/dist/styles.min.css";
import routerProvider from "@pankod/refine-react-router-v6";

import dataProvider, { GraphQLClient } from "@pankod/refine-graphql";

import {
  SubmissionList,
  SubmissionEdit,
  SubmissionData,
} from "pages/form-submissions";

import { UserList, UserData, UserEdit, UserCreate } from "pages/users";
import { LogList } from "pages/logs";
import { LeadList, LeadData, LeadEdit } from "pages/leads";

import { LoginPage } from "pages/login";
import { DashboardList } from "pages/dashboard";

const { Link } = routerProvider;
const client = new GraphQLClient(process.env.REACT_APP_URI ?? "");
const gqlDataProvider = dataProvider(client);

const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const { data } = await gqlDataProvider.custom!({
        url: "",
        method: "post",
        metaData: {
          operation: "login",
          variables: {
            input: {
              value: { email, password },
              type: "UsersPermissionsLoginInput",
              required: true,
            },
          },
          fields: ["token"],
        },
      });

      localStorage.setItem("token", data.token);
      client.setHeader("Authorization", `Bearer ${data.token}`);

      return Promise.resolve();
    } catch (error: any) {
      return Promise.reject(error?.response?.errors[0]?.message);
    }
  },
  logout: async () => {
    localStorage.removeItem("token");
    client.setHeader("Authorization", "");
    return Promise.resolve("/");
  },
  checkError: (error) => {
    if (error) {
      return Promise.reject();
    }
    return Promise.resolve();
  },
  checkAuth: () => {
    const jwt = localStorage.getItem("token");

    if (!jwt) {
      return Promise.reject();
    }

    client.setHeader("Authorization", `Bearer ${jwt}`);

    return Promise.resolve();
  },
  getPermissions: async () => {
    try {
      const { data } = await gqlDataProvider.custom!({
        url: "",
        method: "get",
        metaData: {
          operation: "me",
          fields: ["role"],
        },
      });

      const { role } = data;
      return Promise.resolve({ role });
    } catch (error: any) {
      return Promise.reject(error?.response?.errors[0]?.message);
    }
  },
  getUserIdentity: async () => {
    try {
      const { data } = await gqlDataProvider.custom!({
        url: "",
        method: "get",
        metaData: {
          operation: "me",
          fields: ["email", "role", "id", "name"],
        },
      });
      const { email, role, id, name } = data;

      return Promise.resolve({ email, role, id, name });
    } catch (error: any) {
      return Promise.reject(error?.response?.errors[0]?.message);
    }
  },
};

function App() {
  return (
    <Refine
      LoginPage={LoginPage}
      authProvider={authProvider}
      notificationProvider={notificationProvider}
      Layout={Layout}
      Title={() => (
        <Link to="/">
          <img
            src="/assets/logo.svg"
            alt="Ebridge"
            style={{
              height: "40px",
              margin: "24px auto 32px auto",
              display: "block",
            }}
          />
        </Link>
      )}
      ReadyPage={ReadyPage}
      catchAll={<ErrorComponent />}
      routerProvider={routerProvider}
      dataProvider={gqlDataProvider}
      resources={[
        {
          name: "dashboard",
          list: DashboardList,
        },
        {
          name: "formSubmissions",
          list: SubmissionList,
          edit: SubmissionEdit,
          show: SubmissionData,
        },
        {
          name: "users",
          list: UserList,
          show: UserData,
          edit: UserEdit,
          create: UserCreate,
        },
        {
          name: "leads",
          list: LeadList,
          show: LeadData,
          edit: LeadEdit,
        },
        {
          name: "logs",
          list: LogList,
        },
      ]}
    />
  );
}

export default App;
